import React, { useEffect, useState, useMemo, useRef } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, TextField, Button, CircularProgress, Alert } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import * as Utils from '../lib/utils';

import { useMsal, useAccount } from "@azure/msal-react";
import { protectedResources } from "../config/authConfig";
import CONFIG from '../config';

export default function SalaryTable(props) {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const urlParams = Utils.getParams();

  const isAdmin =
    props &&
    props.userAccount &&
    props.userAccount.access_level &&
    (
      props.userAccount.access_level.indexOf("ADMIN") > -1 ||
      props.userAccount.access_level.indexOf("MODERATOR") > -1
    );
  const selectedManagerEmail = isAdmin ? urlParams.manager_email : "";

  const thisYear = new Date().getFullYear();
  const yearsArray = [];
  for (let i = 2025; i <= (thisYear + 1); i++) {
    yearsArray.push(i);
  }
  yearsArray.reverse();

  const [selectedYear, setSelectedYear] = useState(thisYear);
  const [dimensions, setDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });
  
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [noData, setNoData] = useState(false);
  const [showRangeNumbers, setShowRangeNumbers] = useState(true);
  const [uploadStatus, setUploadStatus] = useState(null); // null | "uploading" | "success" | "error"
  const [uploadMessage, setUploadMessage] = useState("");
  const [enforceRangeMap, setEnforceRangeMap] = useState({});
  const fileInputRef = useRef(null);
 
  const debounce = function (fn, ms) {
    let timer;
    return (_) => {
      clearTimeout(timer);
      timer = setTimeout((_) => {
        timer = null;
        fn.apply(this, arguments);
      }, ms);
    };
  };

  useEffect(() => {
    const debouncedHandleResize = debounce(function handleResize() {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      });
    }, 500);

    window.addEventListener("resize", debouncedHandleResize);

    return (_) => {
      window.removeEventListener("resize", debouncedHandleResize);
    };
  });

  const getColumnClass = (label)=>{
    if(label.indexOf("Group A") > -1){
      return "groupAColumn";
    }
    if(label.indexOf("Group B") > -1){
      return "groupBColumn";      
    }
    if(label.indexOf("Group C") > -1){
      return "groupCColumn";      
    }
  }

  const getColumnFlexMode = (label)=>{
    if(label.indexOf("Position Title") > -1 || label.indexOf("Labor Category") > -1){
      return 1;
    }else{
      return "none";
    }
  }

  const loadData = async (year) => {
    let xlsDataPath = "/api/salary_range/xls_ranges?year=" + year;
    if(selectedManagerEmail){
      xlsDataPath = xlsDataPath + "&manager_email=" + selectedManagerEmail;
    }

    try {
      const response = await Utils.getData(
        xlsDataPath,
        instance,
        account
      );

      if (response.noData) {
        setNoData(true);
        setRows([]);
        setColumns([]);
        return;
      }

      setNoData(false);
      const { headers, data } = response;

      // Fetch aliases to get enforce_range state (admin only)
      let aliasEnforceMap = {};
      if (isAdmin) {
        try {
          const aliases = await Utils.getData('/api/job_title_alias', instance, account);
          if (aliases && aliases.length) {
            for (let i = 0; i < aliases.length; i++) {
              const key = (aliases[i].salary_range_title || "").toLowerCase();
              if (!aliasEnforceMap[key]) {
                aliasEnforceMap[key] = aliases[i].enforce_range || false;
              }
            }
          }
        } catch (e) {
          console.error("Error fetching aliases for enforce_range:", e);
        }
        setEnforceRangeMap(aliasEnforceMap);
      }

      // Build MUI DataGrid columns
      const muiColumns = headers.map((header, index) => ({
        field: header && header.key ? header.key : ("col_"+index),
        headerName: header && header.label ? header.label : ("col_"+index),
        flex: getColumnFlexMode(header && header.label ? header.label : ""),
        minWidth: 150,
        sortable: true,
        cellClassName: getColumnClass(header && header.label ? header.label : "")
      }));

      // Add enforce range checkbox column for admins
      if (isAdmin && !selectedManagerEmail) {
        muiColumns.push({
          field: "enforce_range",
          headerName: "PE or LG required?",
          width: 140,
          sortable: true,
        });
      }

      setColumns(muiColumns);

      // DataGrid requires a top-level `id`
      const rowsWithId = data.map((row, index) => ({
        id: index,
        ...row,
      }));

      setRows(rowsWithId);
    } catch (error) {
      console.error("Error loading salary data:", error);
      setNoData(true);
      setRows([]);
      setColumns([]);
    }
  };

  // Compute final columns with reactive enforce_range checkbox and range visibility
  const displayColumns = useMemo(() => {
    if (!columns.length) return [];

    return columns
      .filter((col) => {
        // If showRangeNumbers is off, hide the salary range number columns
        if (!showRangeNumbers) {
          const rangeFields = ["col_2", "col_3", "col_4", "col_8", "col_9", "col_10", "col_14", "col_15", "col_16"];
          if (rangeFields.includes(col.field)) return false;
        }
        return true;
      })
      .map((col) => {
        if (col.field === "enforce_range") {
          return {
            ...col,
            renderCell: (params) => {
              const posTitle = (params.row.col_1 || "").toLowerCase();
              const isChecked = enforceRangeMap[posTitle] || false;
              return (
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => {
                    handleEnforceRangeToggle(params.row.col_1, e.target.checked);
                  }}
                />
              );
            },
          };
        }
        return col;
      });
  }, [columns, enforceRangeMap, showRangeNumbers]);

  const handleEnforceRangeToggle = async (salaryRangeTitle, checked) => {
    try {
      await Utils.putData(
        "/api/job_title_alias/enforce_range",
        { salary_range_title: salaryRangeTitle, enforce_range: checked },
        instance,
        account
      );
      // Update local state
      setEnforceRangeMap((prev) => ({
        ...prev,
        [(salaryRangeTitle || "").toLowerCase()]: checked,
      }));
      // Force re-render of rows by updating them
      setRows((prev) => [...prev]);
    } catch (error) {
      console.error("Error toggling enforce range:", error);
      alert("There was an error updating the enforce range setting.");
    }
  };

  useEffect(() => {
    loadData(selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const filteredRows = useMemo(() => {
    if (!filterText) return rows;

    const lower = filterText.toLowerCase();

    return rows.filter((row) =>
      Object.values(row).some(
        (value) =>
          typeof value === "string" &&
          value.toLowerCase().includes(lower)
      )
    );
  }, [rows, filterText]);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("year", selectedYear);

    setUploadStatus("uploading");
    setUploadMessage("");

    try {
      const resp = await Utils.postFile(
        "/api/salary_range/xls_upload",
        formData,
        instance,
        account
      );
      const result = await resp.json();
      if (result.result === "SUCCESS") {
        setUploadStatus("success");
        // Reload the grid data from the new file
        await loadData(selectedYear);
        
        let successMessage = result.message || "File uploaded and data refreshed successfully for " + selectedYear + ".";
        if (result.rangeErrors && result.rangeErrors.length > 0) {
          successMessage += "\n\nThe following titles had errors and were skipped:";
          const errorDetails = result.rangeErrors.map(err => 
            "• " + err.job_title + " (" + err.department + " " + err.group + "): " + err.reason
          );
          // Deduplicate error messages
          const uniqueErrors = [...new Set(errorDetails)];
          alert(successMessage + "\n" + uniqueErrors.join("\n"));
        }
        setUploadMessage(result.message || "File uploaded and data refreshed successfully for " + selectedYear + ".");
      } else {
        setUploadStatus("error");
        setUploadMessage(result.message || "Upload failed.");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setUploadStatus("error");
      setUploadMessage("An error occurred during upload.");
    }
  };

  return (
    <Box sx={{ height: (dimensions.height-120), width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <strong>Year:</strong>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{ padding: "6px", fontSize: "14px" }}
          >
            {yearsArray.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </Box>
        <TextField
          label="Filter"
          variant="outlined"
          size="small"
          fullWidth
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          sx={{ 
            maxWidth: '300px',
            marginTop: '3px',
            marginBottom: '-3px',
          }}
        />
        <Button
          variant="contained"
          onClick={()=>{
            const groupOffices = "Group A: Pipelines, Boston, Redmond, San Diego, Seattle, Southborough\n\r" +  
              "Group B: Bellingham, Cary, Lake Oswego, Guam, Portland, Tacoma \n\r" +   
              "Group C: Baton Rouge, Boise, Charleston, Kennewick, Salt Lake City, Spokane, St. Louis, Springfield";
            window.alert(groupOffices);
          }}
        >Display Group Offices</Button>
        {isAdmin && !selectedManagerEmail && (
          <Button
            variant="outlined"
            onClick={() => setShowRangeNumbers(!showRangeNumbers)}
            sx={{ whiteSpace: "nowrap" }}
          >
            {showRangeNumbers ? "Hide Range Numbers" : "Show Range Numbers"}
          </Button>
        )}
        {isAdmin && !selectedManagerEmail && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <Button
              variant="contained"
              startIcon={uploadStatus === "uploading" ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
              onClick={handleUploadClick}
              disabled={uploadStatus === "uploading"}
              sx={{ whiteSpace: "nowrap" }}
            >
              {uploadStatus === "uploading" ? "Uploading..." : "Upload Excel (" + selectedYear + ")"}
            </Button>
          </>
        )}
      </Box>

      {uploadStatus === "success" && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadStatus(null)}>
          {uploadMessage}
        </Alert>
      )}
      {uploadStatus === "error" && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadStatus(null)}>
          {uploadMessage}
        </Alert>
      )}

      {noData ? (
        <Box sx={{ p: 4, textAlign: "center", color: "#666" }}>
          <h3>No salary range data available for {selectedYear}.</h3>
          <p>Upload an Excel file to populate salary ranges for this year.</p>
        </Box>
      ) : (
        <DataGrid
          rows={filteredRows}
          columns={displayColumns}
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
          }}
          sx={{
            '& .MuiTablePagination-selectLabel': {
              marginTop: '15px'
            },
            '& .MuiTablePagination-displayedRows': {
              marginTop: '15px'
            }
          }}
        />
      )}
    </Box>
  );
}
